import React, { useState, useEffect } from 'react';
import { X, User, Settings, CreditCard, Shield, LifeBuoy, AlertTriangle, Heart, Smartphone, CheckCircle2, Link as LinkIcon, Info, HelpCircle, Save, Activity, CheckCircle, Trash, Upload, Plus, Download, Sparkles, Layers, Zap, FileText, Gauge, Brain, Stethoscope, ShieldCheck, MessageSquare, ArrowRight, Target, Compass, Clock, Ban, Flame } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { StravaConnectButton, PoweredByStrava } from './StravaAssets';
import { connectProvider } from '../utils/capacitorUtils';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

const ALL_AVAILABLE_MODULES = [
  { id: 'lastActivity', label: 'Último Entrenamiento', desc: 'Resumen rápido y análisis IA de tu sesión más reciente', category: 'Rendimiento' },
  { id: 'fitnessChart', label: 'Forma Atlética Profesional (PMC)', desc: 'Gráfico detallado de carga crónica, aguda y estrés (CTL/ATL/TSB)', category: 'Rendimiento' },
  { id: 'aiReportCard', label: 'Informe Fisiológico IA', desc: 'Informe predictivo semanal de fatiga y forma generado por IA', category: 'Rendimiento' },
  { id: 'performanceEvolution', label: 'Evolución de Rendimiento', desc: 'Tendencias clave, fatiga y récords históricos', category: 'Rendimiento' },
  { id: 'sportRadar', label: 'Perfil Deportivo (Gráfico Radar)', desc: 'Radar de fortalezas y debilidades físicas', category: 'Rendimiento' },
  { id: 'intensityChart', label: 'Distribución de Intensidad', desc: 'Tiempo acumulado por zonas de potencia y cardio', category: 'Rendimiento' },
  
  { id: 'trainingReadiness', label: 'Predisposición al Entrenamiento', desc: 'Indicador de recuperación diaria basado en biometría', category: 'Salud y Fisiología' },
  { id: 'recoveryPanel', label: 'Calidad del Sueño y Descanso', desc: 'Desglose detallado de fases del sueño y frecuencia cardíaca en reposo', category: 'Salud y Fisiología' },
  { id: 'healthSidebar', label: 'Biometría de Salud y Peso', desc: 'Registro histórico de peso corporal, sueño general y RHR', category: 'Salud y Fisiología' },
  { id: 'aerobicEfficiency', label: 'Eficiencia Aeróbica', desc: 'Relación entre potencia y pulsaciones a lo largo del tiempo', category: 'Salud y Fisiología' },

  { id: 'trainingCalendar', label: 'Calendario de Actividades', desc: 'Planificador mensual de entrenamientos planeados y completados', category: 'Objetivos y Agenda' },
  { id: 'recentActivities', label: 'Actividades Recientes', desc: 'Listado de tus últimas actividades registradas', category: 'Objetivos y Agenda' },
  { id: 'activityBreakdown', label: '📊 Desglose por Deportes', desc: 'Distribución detallada de actividades por deporte con vista diaria, mensual y anual', category: 'Objetivos y Agenda' },
  { id: 'historicalStats', label: '📈 Historial Completo', desc: 'Estadísticas acumuladas de todos tus años. Ideal si llevas tiempo en Strava', category: 'Objetivos y Agenda' }
];

const AccountSettingsModal = ({ 
  isOpen, 
  onClose, 
  visibleModules, 
  setVisibleModules, 
  appMode, 
  setAppMode,
  userProfile,
  setUserProfile
}) => {
  const [activeTab, setActiveTab] = useState(appMode === 'coach' ? 'account' : 'physiology'); // Puesto en primer lugar por defecto si es atleta
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Coach specific states
  const [invitations, setInvitations] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [alertsConfig, setAlertsConfig] = useState({ rpeThreshold: 8, tsbThreshold: -30, hrvDropThreshold: 15, notifyByEmail: true });
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Apple Health Shortcut State
  const [showAppleSetup, setShowAppleSetup] = useState(false);
  const [appleToken, setAppleToken] = useState('');

  // Account tab state
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  // Coupon state
  const [couponCode, setCouponCode] = useState('');

  // Strava specific state
  const [stravaAllowedTypes, setStravaAllowedTypes] = useState(['Run', 'Ride', 'VirtualRide', 'Swim', 'WeightTraining', 'Other']);
  const [showStravaDisconnectConfirm, setShowStravaDisconnectConfirm] = useState(false);

  // Medical report state
  const [uploadingMedicalReport, setUploadingMedicalReport] = useState(false);

  // Physiology state
  const [physiology, setPhysiology] = useState({
    hrZones: { z1: 114, z2: 126, z3: 137, z4: 151, z5: 200 },
    ftp: 200,
    wPrime: 20, // en kJ
    maxHr: 190,
    biometrics: { weight: '', height: '', rhr: '', lthr: '', runFtp: '', gender: '' }
  });

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/profile?_t=${Date.now()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      setUserProfile(data);
    } catch (e) {
      console.error('Error fetching profile:', e);
    }
  };

  const handleDisconnectWithings = async () => {
    if (!window.confirm("¿Seguro que quieres desconectar Withings? Perderás la sincronización automática de peso, grasa y sueño.")) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/withings/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchProfile();
        alert("Withings desconectado correctamente.");
      } else {
        alert("Error al desconectar Withings");
      }
    } catch (e) {
      console.error(e);
      alert("Error de conexión");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setNewEmail(userProfile?.email || '');
      setMessage('');
      setError('');
      setDeleteConfirm(false);
      
      const prefs = typeof userProfile?.preferences === 'string' ? JSON.parse(userProfile.preferences) : userProfile?.preferences;
      let initialWeight = prefs?.physiology?.biometrics?.weight || prefs?.weight || '';
      let initialFat = prefs?.physiology?.biometrics?.bodyFat || prefs?.bodyFat || '';
      let initialVo2 = prefs?.physiology?.biometrics?.vo2max || prefs?.physiology?.vo2max || prefs?.vo2max || '';

      const medCtx = prefs?.medicalContext || '';
      if (!initialWeight && medCtx) {
        const wMatch = String(medCtx).match(/(?:peso|weight)(?:\s+corporal)?\s*[:=]?\s*(\d+(?:[.,]\d+)?)\s*kg/i);
        if (wMatch) initialWeight = parseFloat(wMatch[1].replace(',', '.'));
      }
      if (!initialFat && medCtx) {
        const fMatch = String(medCtx).match(/(?:grasa|fat|índice de grasa)\s*[:=]?\s*(\d+(?:[.,]\d+)?)\s*%/i);
        if (fMatch) initialFat = parseFloat(fMatch[1].replace(',', '.'));
      }
      if (!initialVo2 && medCtx) {
        const vo2Match = String(medCtx).match(/vo2\s*(?:max|máx)?\s*[:=]?\s*(\d+(?:[.,]\d+)?)/i);
        if (vo2Match) initialVo2 = parseFloat(vo2Match[1].replace(',', '.'));
      }

      setPhysiology({
        hrZones: prefs?.physiology?.hrZones || { z1: 114, z2: 126, z3: 137, z4: 151, z5: 200 },
        ftp: prefs?.physiology?.ftp || 200,
        wPrime: prefs?.physiology?.wPrime ? Math.round(prefs.physiology.wPrime / 1000) : 20,
        maxHr: prefs?.physiology?.maxHr || 190,
        biometrics: {
          weight: initialWeight,
          height: prefs?.physiology?.biometrics?.height || '',
          gender: prefs?.physiology?.biometrics?.gender || '',
          rhr: prefs?.physiology?.biometrics?.rhr || '',
          lthr: prefs?.physiology?.biometrics?.lthr || '',
          runFtp: prefs?.physiology?.biometrics?.runFtp || '',
          bodyFat: initialFat,
          vo2max: initialVo2
        }
      });
      
      if (prefs?.stravaAllowedTypes) {
        setStravaAllowedTypes(prefs.stravaAllowedTypes);
      } else {
        setStravaAllowedTypes(['Run', 'Ride', 'VirtualRide', 'Swim', 'WeightTraining', 'Other']);
      }
    }
  }, [isOpen, userProfile]);

  const fetchInvitations = async () => {
    try {
      setLoadingInvites(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/invitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(data);
      }
    } catch (e) {
      console.error('Error fetching invitations:', e);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleRevokeInvitation = async (id) => {
    if (!window.confirm('¿Seguro que deseas revocar esta invitación? El enlace ya no funcionará.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/invitations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMessage('Invitación revocada correctamente');
        fetchInvitations();
      } else {
        const data = await res.json();
        setError(data.error || 'Error al revocar invitación');
      }
    } catch (e) {
      setError('Error al conectar con el servidor');
    }
  };

  const fetchAlertsConfig = async () => {
    try {
      setLoadingAlerts(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/alerts-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlertsConfig(data);
      }
    } catch (e) {
      console.error('Error fetching alerts config:', e);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const handleSaveAlertsConfig = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage('');
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/alerts-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(alertsConfig)
      });
      if (res.ok) {
        setMessage('Configuración de alertas guardada con éxito.');
      } else {
        const data = await res.json();
        setError(data.error || 'Error al guardar la configuración');
      }
    } catch (e) {
      setError('Error al guardar en el servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && activeTab === 'invitations') {
      fetchInvitations();
    }
    if (isOpen && activeTab === 'alerts') {
      fetchAlertsConfig();
    }
  }, [isOpen, activeTab]);

  if (!isOpen) return null;

  


  const handleUploadMedicalReport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. El límite es 10MB.');
      return;
    }

    setUploadingMedicalReport(true);
    setError('');
    setMessage('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/user/medical-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error subiendo informe médico');

      setMessage('Informe analizado con éxito por la IA.');
      // Update local profile state
      if (userProfile && data.medicalContext) {
        setUserProfile({
          ...userProfile,
          preferences: {
            ...userProfile.preferences,
            medicalContext: data.medicalContext
          }
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setUploadingMedicalReport(false);
      // Reset input
      e.target.value = null;
    }
  };

  const handleDeleteMedicalReport = async () => {
    if (!window.confirm('¿Seguro que quieres eliminar tu contexto médico de la IA?')) return;
    
    try {
      const response = await fetch(`${API_URL}/user/medical-report`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error borrando informe médico');

      setMessage('Contexto médico eliminado correctamente.');
      if (userProfile && userProfile.preferences) {
        const updatedPrefs = { ...userProfile.preferences };
        delete updatedPrefs.medicalContext;
        setUserProfile({
          ...userProfile,
          preferences: updatedPrefs
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleStravaDisconnect = async () => {
    try {
      setLoading(true);
      setShowStravaDisconnectConfirm(false);
      const res = await fetch(`${API_URL}/auth/strava/disconnect`, { 
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setUserProfile(prev => ({ ...prev, stravaConnected: false }));
        setMessage('Strava desconectado y desautorizado exitosamente.');
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'No se pudo desconectar Strava.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleHealthToggle = async () => {
    if (userProfile?.preferences?.appleHealthConnected) {
      // Si ya está conectado, permitir desconectar
      try {
        const response = await fetch(`${API_URL}/user/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ appleHealthConnected: false })
        });
        if (response.ok) {
          alert("Apple Health desconectado.");
          window.location.reload();
        }
      } catch(e) { console.error(e); }
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ appleHealthConnected: true })
      });
      if (response.ok) {
        alert("¡Apple Health conectado! La sincronización comenzará ahora.");
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleGoogleHealthToggle = () => {
    alert("Para conectar Google Health Connect, por favor usa la app nativa de Plataforma Deportiva en tu dispositivo Android.");
  };


  const handleToggleRecoveryMode = async () => {
    try {
      const newState = !userProfile?.preferences?.recoveryMode;
      const response = await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ recoveryMode: newState })
      });
      if (response.ok) {
        if (setUserProfile && userProfile) {
          setUserProfile({
            ...userProfile,
            preferences: {
              ...(typeof userProfile.preferences === 'string' ? JSON.parse(userProfile.preferences) : userProfile.preferences),
              recoveryMode: newState
            }
          });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleModule = (moduleId) => {
    if (Array.isArray(visibleModules)) {
      if (visibleModules.includes(moduleId)) {
        setVisibleModules(visibleModules.filter(id => id !== moduleId));
      } else {
        setVisibleModules([...visibleModules, moduleId]);
      }
    } else {
      // Fallback
      setVisibleModules([moduleId]);
    }
  };

  const handleUpdatePlan = async (targetPlan) => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/plan`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan: targetPlan })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cambiar el plan');
      setMessage(`Plan cambiado con éxito a ${targetPlan}. Reiniciando aplicación para aplicar los cambios...`);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/account`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newEmail: newEmail !== userProfile.email ? newEmail : undefined,
          newPassword: newPassword || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al actualizar');
      setMessage('Cuenta actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      setError('Escribe tu contraseña o la palabra ELIMINAR para confirmar la baja');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const isWord = deletePassword.trim().toUpperCase() === 'ELIMINAR';
      const res = await fetch(`${API_URL}/user/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(isWord ? { confirmation: 'ELIMINAR' } : { password: deletePassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al eliminar');
      // Force logout
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleRevokeCoach = async () => {
    if (!window.confirm('¿Seguro que quieres desvincularte de tu entrenador actual? Dejará de tener acceso a tus datos.')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/coach`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al revocar acceso');
      setMessage('Acceso de entrenador revocado');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSavePhysiology = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...physiology,
        wPrime: (parseInt(physiology.wPrime) || 20) * 1000
      };
      const res = await fetch(`${API_URL}/user/physiology`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Error guardando perfil fisiológico');
      setMessage('Perfil fisiológico guardado correctamente. Recarga para aplicar cambios.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Personalización al final
  const tabs = [
    { id: 'physiology', label: 'Perfil Fisiológico', icon: <Heart size={18} /> },
    { id: 'integrations', label: 'Apps y Dispositivos', icon: <Smartphone size={18} /> },
    { id: 'guide', label: 'Guía y Ventajas IA', icon: <Sparkles size={18} /> },
    { id: 'invitations', label: 'Invitaciones', icon: <LinkIcon size={18} /> },
    { id: 'coachHelp', label: 'Guía de Ayuda', icon: <HelpCircle size={18} /> },
    { id: 'account', label: 'Mi Cuenta', icon: <User size={18} /> },
    { id: 'membership', label: 'Membresía', icon: <CreditCard size={18} /> },
    { id: 'privacy', label: 'Privacidad', icon: <Shield size={18} /> },
    { id: 'support', label: 'Soporte Técnico', icon: <LifeBuoy size={18} /> },
    { id: 'panel', label: 'Personalización', icon: <Settings size={18} /> }
  ];

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal-panel">
        {/* Header */}
        <div className="settings-modal-header">
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>Ajustes Personales</h2>
            <p className="text-muted" style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Ajustes y preferencias de tu cuenta</p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
            width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', transition: 'all 0.2s ease'
          }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
             onMouseOut={e => e.currentTarget.style.background = 'none'}>
            <X size={24} />
          </button>
        </div>

        {/* Content Area */}
        <div className="settings-content-wrapper">
          
          {/* Sidebar */}
          <div className="settings-sidebar-container">
            <div className="settings-sidebar" onScroll={(e) => {
              if (e.currentTarget.scrollLeft > 20) {
                e.currentTarget.parentElement.classList.add('is-scrolled');
              } else {
                e.currentTarget.parentElement.classList.remove('is-scrolled');
              }
            }}>
            {tabs.map(tab => {
              // Hide tabs not relevant for coaches
              if (appMode === 'coach' && ['physiology', 'integrations', 'panel', 'privacy'].includes(tab.id)) return null;

              // Hide tabs only relevant for coaches
              if (appMode !== 'coach' && ['invitations', 'alerts', 'coachHelp'].includes(tab.id)) return null;

              // Hide privacy tab for non-athletes
              if (tab.id === 'privacy' && appMode !== 'athlete') return null;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMessage(''); setError(''); }}
                  className={activeTab === tab.id ? 'active-tab' : ''}
                  style={{
                    background: activeTab === tab.id ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid',
                    borderColor: activeTab === tab.id ? 'var(--health-cyan)' : 'rgba(255,255,255,0.05)',
                    color: activeTab === tab.id ? 'white' : 'var(--text-muted)',
                    padding: '0.75rem 1.25rem', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    transition: 'all 0.2s ease', borderRadius: '24px',
                    fontSize: '0.9rem', whiteSpace: 'nowrap',
                    fontWeight: activeTab === tab.id ? '600' : 'normal'
                  }}
                  onMouseOver={e => { if(activeTab !== tab.id) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseOut={e => { if(activeTab !== tab.id) e.currentTarget.style.background = 'transparent' }}
                >
                  {tab.icon} {tab.label}
                </button>
              );
            })}
            </div>
          </div>

          {/* Main Content */}
          <div className="settings-main-content">
            {message && <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '8px', marginBottom: '1.5rem' }}>{message}</div>}
            {error && <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', marginBottom: '1.5rem' }}>{error}</div>}

            {activeTab === 'panel' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Módulos del Dashboard</h3>
                <p className="text-muted" style={{ marginBottom: '1.5rem', fontSize: '1rem' }}>
                  Selecciona qué paneles quieres ver activos en tu panel de control principal.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {['Rendimiento', 'Salud y Fisiología', 'Objetivos y Agenda'].map(category => {
                    const categoryModules = ALL_AVAILABLE_MODULES.filter(m => m.category === category);
                    return (
                      <div key={category}>
                        <h4 style={{
                          color: '#ff5722', 
                          fontSize: '0.9rem', 
                          fontWeight: 'bold', 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
                          paddingBottom: '0.4rem', 
                          marginBottom: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>{category}</h4>
                        <div className="settings-grid-2col" style={{ gap: '0.8rem' }}>
                          {categoryModules.map(module => {
                            const isChecked = Array.isArray(visibleModules) && visibleModules.includes(module.id);
                            return (
                              <div 
                                key={module.id} 
                                onClick={() => handleToggleModule(module.id)}
                                style={{
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  padding: '0.75rem 1rem',
                                  background: isChecked ? 'rgba(255, 87, 34, 0.08)' : 'rgba(255,255,255,0.02)',
                                  border: isChecked ? '1px solid rgba(255, 87, 34, 0.3)' : '1px solid rgba(255,255,255,0.05)',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <div style={{ flex: 1, paddingRight: '0.5rem' }}>
                                  <div style={{ fontWeight: '600', color: isChecked ? 'white' : 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.1rem' }}>
                                    {module.label}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', lineHeight: '1.2' }}>
                                    {module.desc}
                                  </div>
                                </div>
                                
                                {/* Switch indicator */}
                                <div style={{
                                  width: '36px',
                                  height: '20px',
                                  borderRadius: '10px',
                                  background: isChecked ? '#ff5722' : 'rgba(255,255,255,0.1)',
                                  position: 'relative',
                                  transition: 'background 0.3s ease',
                                  flexShrink: 0
                                }}>
                                  <div style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    position: 'absolute',
                                    top: '3px',
                                    left: isChecked ? '19px' : '3px',
                                    transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                  }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div>
                    <h3 className="text-lg" style={{ margin: 0 }}>Hub de Integraciones</h3>
                    <p className="text-muted text-sm" style={{ margin: '0.2rem 0 0 0' }}>Gestiona tus conexiones con wearables y aplicaciones externas.</p>
                  </div>
                </div>

                {/* HUB PRINCIPAL */}
                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '1rem', marginTop: '0', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Hub Principal (Recomendado)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
                  
                  {/* Strava */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#FC4C02', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}>S</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'white' }}>Strava (Entrenamiento)</h4>
                        <span style={{ fontSize: '0.8rem', color: userProfile?.stravaConnected ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: userProfile?.stravaConnected ? '#10b981' : 'gray' }}></span>
                          {userProfile?.stravaConnected ? 'Conectado' : 'No conectado'}
                        </span>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Sincroniza tus actividades GPS, frecuencia cardíaca y potencia desde cualquier reloj compatible (Garmin, Suunto, Coros, Wahoo, etc.).</p>
                    {userProfile?.stravaConnected ? (
                      showStravaDisconnectConfirm ? (
                        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', padding: '0.75rem' }}>
                          <p style={{ margin: 0, fontSize: '0.78rem', color: '#fca5a5', lineHeight: 1.3 }}>
                            ¿Seguro que deseas desconectar Strava? Se pausará la sincronización de nuevas actividades.
                          </p>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type="button"
                              onClick={handleStravaDisconnect}
                              disabled={loading}
                              style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer' }}
                            >
                              {loading ? 'Desconectando...' : 'Sí, desconectar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowStravaDisconnectConfirm(false)}
                              disabled={loading}
                              style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', background: 'rgba(255,255,255,0.1)', color: 'var(--text-main)', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.75rem', cursor: 'pointer' }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button 
                          type="button"
                          onClick={() => setShowStravaDisconnectConfirm(true)}
                          disabled={loading}
                          style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 'bold', cursor: 'pointer', background: 'transparent', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s' }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                        >
                          <LinkIcon size={16} /> Desconectar
                        </button>
                      )
                    ) : (
                      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
                        <StravaConnectButton 
                          onClick={() => connectProvider('strava', userProfile.id, API_URL)}
                          disabled={loading}
                          style={{ width: '100%' }}
                        />
                        <PoweredByStrava />
                      </div>
                    )}
                  </div>

                {/* Apple Health (Nativo) */}
                  <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', opacity: Capacitor.getPlatform() === 'ios' ? 1 : 0.6 }}>
                    {Capacitor.getPlatform() !== 'ios' && (
                      <div style={{ position: 'absolute', top: 0, right: 0, background: '#ff2d55', color: 'white', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.2rem 0.5rem', borderBottomLeftRadius: '8px' }}>SOLO EN IPHONE</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255, 45, 85, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Heart size={24} color="white" fill="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>Salud (Apple Health)</h4>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                          Sincronización Nativa
                        </span>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Agregará sueño, pasos, VFC y peso directamente de tu iPhone sin configuraciones adicionales.</p>
                    {Capacitor.getPlatform() === 'ios' ? (
                      <button 
                        onClick={handleAppleHealthToggle}
                        style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: userProfile?.preferences?.appleHealthConnected ? 'none' : '1px dashed rgba(255,255,255,0.2)', background: userProfile?.preferences?.appleHealthConnected ? 'rgba(16, 185, 129, 0.1)' : 'transparent', color: userProfile?.preferences?.appleHealthConnected ? '#10b981' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                      >
                        {userProfile?.preferences?.appleHealthConnected ? <><CheckCircle2 size={16} /> Conectado</> : <><LinkIcon size={16} /> Conectar ahora</>}
                      </button>
                    ) : (
                      <button disabled style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-muted)', cursor: 'not-allowed' }}>Requiere App iOS</button>
                    )}
                  </div>

                  {/* Google Health / Health Connect (Nativo Android) */}
                  <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', opacity: Capacitor.getPlatform() === 'android' ? 1 : 0.6 }}>
                    {Capacitor.getPlatform() !== 'android' && (
                      <div style={{ position: 'absolute', top: 0, right: 0, background: '#4285F4', color: 'white', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.2rem 0.5rem', borderBottomLeftRadius: '8px' }}>SOLO EN ANDROID</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(66, 133, 244, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Heart size={24} color="white" fill="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>Google Health Connect</h4>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                          Sincronización Nativa
                        </span>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Agregará sueño, pasos, VFC y peso directamente de tu dispositivo Android.</p>
                    {Capacitor.getPlatform() === 'android' ? (
                      <button 
                        onClick={handleGoogleHealthToggle}
                        style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: userProfile?.preferences?.googleHealthConnected ? 'none' : '1px dashed rgba(255,255,255,0.2)', background: userProfile?.preferences?.googleHealthConnected ? 'rgba(16, 185, 129, 0.1)' : 'transparent', color: userProfile?.preferences?.googleHealthConnected ? '#10b981' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                      >
                        {userProfile?.preferences?.googleHealthConnected ? <><CheckCircle2 size={16} /> Conectado</> : <><LinkIcon size={16} /> Conectar ahora</>}
                      </button>
                    ) : (
                      <button disabled style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-muted)', cursor: 'not-allowed' }}>Requiere App Android</button>
                    )}
                  </div>
                </div>

                {/* SECUNDARIO / LEGACY */}
                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '1rem', marginTop: '0', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Conexiones Secundarias</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1.5rem', marginTop: '-0.5rem' }}>Utiliza estas conexiones individuales si no usas iPhone o prefieres sincronización manual.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '1.5rem' }}>
                  
                  {/* Withings */}
                  <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'black', fontWeight: 'bold', fontSize: '1.2rem', letterSpacing: '-1px' }}>withings</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>Withings</h4>
                        <span style={{ fontSize: '0.8rem', color: userProfile?.withingsConnected ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: userProfile?.withingsConnected ? '#10b981' : 'gray' }}></span>
                          {userProfile?.withingsConnected ? 'Conectado' : 'No conectado'}
                        </span>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Sincroniza directamente tu peso y sueño. Se ignora si Apple Health está activo.</p>
                    <button 
                      onClick={() => !userProfile?.withingsConnected ? connectProvider('withings', userProfile.id, API_URL) : handleDisconnectWithings()}
                      style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: userProfile?.withingsConnected ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(255,255,255,0.1)', fontWeight: 'bold', cursor: 'pointer', background: userProfile?.withingsConnected ? 'rgba(239, 68, 68, 0.05)' : 'transparent', color: userProfile?.withingsConnected ? '#ef4444' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    >
                      {userProfile?.withingsConnected ? <>Desconectar</> : <><LinkIcon size={16} /> Conectar</>}
                    </button>
                  </div>

                  {/* Garmin */}
                  <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(0, 124, 195, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}>▲</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>Garmin / Suunto</h4>
                        <span style={{ fontSize: '0.8rem', color: '#3b82f6' }}>Vía Strava Connect</span>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Para importar entrenamientos de Garmin, Suunto, Polar o Coros, vincula tu reloj a Strava y activa la integración superior.</p>
                    <button disabled style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none', background: 'rgba(59, 130, 246, 0.05)', color: 'rgba(59, 130, 246, 0.6)', cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <Info size={16} /> Usa el puente de Strava
                    </button>
                  </div>

                  {/* Oura */}
                  <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', opacity: 0.6 }}>
                    <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--health-cyan)', color: 'black', fontSize: '0.65rem', fontWeight: 'bold', padding: '0.2rem 0.5rem', borderBottomLeftRadius: '8px' }}>PRÓXIMAMENTE</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 'bold', fontSize: '1.2rem' }}>O</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>Oura Ring</h4>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Métricas avanzadas</span>
                      </div>
                    </div>
                    <button disabled style={{ marginTop: 'auto', width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-muted)', cursor: 'not-allowed' }}>En desarrollo</button>
                  </div>

                </div>

                  {/* Modal Anidado de Apple Health - ELIMINADO */}

                {/* Filtros de Importación de Strava */}
                {userProfile?.stravaConnected && (
                  <div style={{ marginTop: '2rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: 'white' }}>Filtro de Actividades de Strava</h4>
                    <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      Selecciona qué tipos de actividades deseas importar a tu cuenta. Los tipos no seleccionados serán ignorados en la próxima sincronización.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                      {[
                        { id: 'Run', label: 'Running / Carrera' },
                        { id: 'Ride', label: 'Ciclismo' },
                        { id: 'VirtualRide', label: 'Ciclismo Virtual' },
                        { id: 'Swim', label: 'Natación' },
                        { id: 'WeightTraining', label: 'Fuerza / Pesas' },
                        { id: 'Other', label: 'Otras actividades' }
                      ].map(sport => {
                        const isSelected = stravaAllowedTypes.includes(sport.id);
                        
                        return (
                          <label key={sport.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', background: isSelected ? 'rgba(252, 76, 2, 0.1)' : 'rgba(255,255,255,0.05)', padding: '0.75rem 1rem', borderRadius: '8px', border: `1px solid ${isSelected ? '#FC4C02' : 'transparent'}`, transition: 'all 0.2s' }}>
                            <div style={{ width: '40px', height: '24px', background: isSelected ? '#FC4C02' : 'rgba(255,255,255,0.2)', borderRadius: '12px', position: 'relative', transition: 'background 0.3s' }}>
                              <div style={{ width: '20px', height: '20px', background: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: isSelected ? '18px' : '2px', transition: 'left 0.3s' }} />
                            </div>
                            <span style={{ color: isSelected ? 'white' : 'var(--text-muted)', fontSize: '0.9rem', fontWeight: isSelected ? '600' : 'normal' }}>{sport.label}</span>
                            <input 
                              type="checkbox" 
                              checked={isSelected} 
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const checked = e.target.checked;
                                let newAllowed = [...stravaAllowedTypes];
                                if (checked) {
                                  newAllowed.push(sport.id);
                                } else {
                                  newAllowed = newAllowed.filter(id => id !== sport.id);
                                }
                                
                                setStravaAllowedTypes(newAllowed);
                              }} 
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Save Integrations Button */}
                {(userProfile?.stravaConnected || userProfile?.withingsConnected) && (
                  <button 
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        const prefs = typeof userProfile?.preferences === 'string' ? JSON.parse(userProfile.preferences) : userProfile?.preferences;
                        const newPrefs = { ...(prefs || {}), stravaAllowedTypes };
                        
                        const res = await fetch(`${API_URL}/user/preferences`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify(newPrefs)
                        });
                        if (res.ok) {
                          if (setUserProfile) {
                            setUserProfile(prev => ({
                              ...prev,
                              preferences: newPrefs
                            }));
                          }
                          window.dispatchEvent(new CustomEvent('force-sync-activities'));
                          setMessage('Ajustes guardados y actividades actualizadas');
                          setTimeout(() => setMessage(''), 3000);
                        }
                      } catch (err) {
                        setError('Error al guardar ajustes');
                        setTimeout(() => setError(''), 3000);
                      }
                      setLoading(false);
                    }}
                    disabled={loading}
                    style={{
                      marginTop: '2rem', padding: '0.8rem 1.5rem', background: 'var(--health-cyan)', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem'
                    }}
                  >
                    <Save size={18} /> {loading ? 'Guardando...' : 'Guardar Ajustes'}
                  </button>
                )}
              </div>
            )}

            {activeTab === 'physiology' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <div style={{ background: 'rgba(139, 92, 246, 0.1)', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                  <Info size={24} color="#a78bfa" style={{ flexShrink: 0 }} />
                  <p style={{ margin: 0, color: '#e2e8f0', fontSize: '0.85rem', lineHeight: 1.4 }}>
                    <strong>Completa tu perfil.</strong> Si no tienes conectada una báscula inteligente (Withings) o Apple/Google Health, introduce tus datos aquí para precisar los cálculos de carga y el análisis de la IA.
                  </p>
                </div>

                {/* Biometría Básica */}
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={18} color="var(--health-cyan)" /> Biometría Básica
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '1rem' }}>
                    
                    {/* Peso */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <div>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          Peso
                          { (userProfile?.withingsConnected || userProfile?.preferences?.appleHealthConnected) && 
                            <span title="Sincronizado vía dispositivo externo (puedes ajustar el valor base)" style={{ display: 'flex', color: 'var(--health-cyan)' }}><LinkIcon size={12}/></span>
                          }
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="number" 
                          step="0.1"
                          placeholder="Ej: 75.5"
                          value={physiology.biometrics?.weight || ''} 
                          onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, weight: parseFloat(e.target.value) || ''}})}
                          style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>kg</span>
                      </div>
                    </div>

                    {/* % Grasa Corporal */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>% Grasa Corporal</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="number" 
                          step="0.1"
                          placeholder="Ej: 14.5"
                          value={physiology.biometrics?.bodyFat || ''} 
                          onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, bodyFat: parseFloat(e.target.value) || ''}})}
                          style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>%</span>
                      </div>
                    </div>

                    {/* VO2 Máx */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>VO₂ Máx</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="number" 
                          step="0.1"
                          placeholder="Ej: 52.0"
                          value={physiology.biometrics?.vo2max || ''} 
                          onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, vo2max: parseFloat(e.target.value) || ''}})}
                          style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>ml/kg</span>
                      </div>
                    </div>

                    {/* Altura */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Altura</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="number" 
                          placeholder="Ej: 180"
                          value={physiology.biometrics?.height || ''} 
                          onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, height: parseInt(e.target.value) || ''}})}
                          style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>cm</span>
                      </div>
                    </div>

                    {/* Género */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Género</span>
                      <select 
                        value={physiology.biometrics?.gender || ''} 
                        onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, gender: e.target.value}})}
                        style={{ width: '100px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', fontSize: '0.85rem' }}
                      >
                        <option value="" style={{color: 'black'}}>Seleccionar</option>
                        <option value="male" style={{color: 'black'}}>Hombre</option>
                        <option value="female" style={{color: 'black'}}>Mujer</option>
                      </select>
                    </div>

                    {/* FC Basal */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>FC Basal (RHR)</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                          type="number" 
                          placeholder="Ej: 50"
                          value={physiology.biometrics?.rhr || ''} 
                          onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, rhr: parseInt(e.target.value) || ''}})}
                          style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>lpm</span>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="settings-grid-2col" style={{ gap: '1.5rem' }}>
                  {/* Zonas Cardíacas */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Heart size={18} color="#ef4444" /> Zonas Cardíacas
                      </h3>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.1)', padding: '0.2rem 0.5rem', borderRadius: '8px' }}>Pulsaciones (lpm)</span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {[
                        { id: 'z1', label: 'Z1 (Recuperación)' },
                        { id: 'z2', label: 'Z2 (Fondo)' },
                        { id: 'z3', label: 'Z3 (Tempo)' },
                        { id: 'z4', label: 'Z4 (Umbral)' },
                        { id: 'z5', label: 'Z5 (VO2 Max)' }
                      ].map((zone) => (
                        <div key={zone.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{zone.label}</span>
                          <input 
                            type="number" 
                            value={physiology.hrZones[zone.id]} 
                            onChange={(e) => setPhysiology({...physiology, hrZones: {...physiology.hrZones, [zone.id]: parseInt(e.target.value) || 0}})}
                            style={{ width: '70px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Otras Métricas */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Activity size={18} color="var(--health-cyan)" /> Rendimiento
                    </h3>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>FTP (Ciclismo)</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Umbral Potencia Funcional</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            value={physiology.ftp} 
                            onChange={(e) => setPhysiology({...physiology, ftp: parseInt(e.target.value) || 0})}
                            style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>W</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>rFTPw (Carrera)</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Potencia Running (Stryd/Apple)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            placeholder="Ej: 300"
                            value={physiology.biometrics?.runFtp || ''} 
                            onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, runFtp: parseInt(e.target.value) || ''}})}
                            style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>W</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>W' (Batería Anaeróbica)</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Reserva sobre FTP (Modelo Skiba)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            value={physiology.wPrime || 20} 
                            onChange={(e) => setPhysiology({...physiology, wPrime: parseInt(e.target.value) || 20})}
                            style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#c084fc', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>kJ</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>FC Máxima</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Tope de pulsaciones</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            value={physiology.maxHr} 
                            onChange={(e) => setPhysiology({...physiology, maxHr: parseInt(e.target.value) || 0})}
                            style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>lpm</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>FC Umbral Lactato (LTHR)</span>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Para Zonas Avanzadas</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input 
                            type="number" 
                            placeholder="Ej: 165"
                            value={physiology.biometrics?.lthr || ''} 
                            onChange={(e) => setPhysiology({...physiology, biometrics: {...physiology.biometrics, lthr: parseInt(e.target.value) || ''}})}
                            style={{ width: '80px', padding: '0.4rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>lpm</span>
                        </div>
                      </div>

                      {/* Vista previa Zonas Coggan Potencia */}
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '0.75rem', borderRadius: '10px', marginTop: '0.25rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.4rem', fontWeight: 'bold' }}>
                          Zonas Coggan (calculadas con {physiology.ftp || 200} W):
                        </span>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '4px', fontSize: '0.7rem' }}>
                          <span style={{ color: '#9ca3af' }}>Z1 Rec: &lt; {Math.round((physiology.ftp || 200) * 0.55)} W</span>
                          <span style={{ color: '#60a5fa' }}>Z2 Fondo: {Math.round((physiology.ftp || 200) * 0.55)}-{Math.round((physiology.ftp || 200) * 0.75)} W</span>
                          <span style={{ color: '#34d399' }}>Z3 Tempo: {Math.round((physiology.ftp || 200) * 0.76)}-{Math.round((physiology.ftp || 200) * 0.90)} W</span>
                          <span style={{ color: '#facc15' }}>Z4 Umbral: {Math.round((physiology.ftp || 200) * 0.91)}-{Math.round((physiology.ftp || 200) * 1.05)} W</span>
                          <span style={{ color: '#fb923c' }}>Z5 VO2max: {Math.round((physiology.ftp || 200) * 1.06)}-{Math.round((physiology.ftp || 200) * 1.20)} W</span>
                          <span style={{ color: '#f87171' }}>Z6 Anaeróbico: {Math.round((physiology.ftp || 200) * 1.21)}-{Math.round((physiology.ftp || 200) * 1.50)} W</span>
                          <span style={{ color: '#c084fc', gridColumn: 'span 2' }}>Z7 Neuromuscular: &gt; {Math.round((physiology.ftp || 200) * 1.51)} W</span>
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={handleSavePhysiology} 
                      disabled={loading} 
                      style={{
                        marginTop: 'auto', width: '100%', padding: '0.8rem', background: 'linear-gradient(135deg, #2563eb, #3b82f6)', 
                        color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
                      }}
                    >
                      {loading ? 'Guardando...' : 'Guardar Perfil Fisiológico'}
                    </button>
                  </div>
                </div>
                
                {/* Salud Médica y Pruebas de Esfuerzo */}
                <div style={{ marginTop: '3rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}>
                  <h3 className="text-lg" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Activity size={18} color="#10b981" /> Informes Médicos y Pruebas de Esfuerzo
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                    La Inteligencia Artificial puede leer tus pruebas de esfuerzo clínicas, de laboratorio o informes médicos. Extraeremos tus verdaderos umbrales (VO2Max, VT1/VT2) y cualquier limitación de salud para tener un contexto clínico 100% real.
                  </p>

                  <div style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                          <Activity size={18} /> Modo Recuperación / Lesión
                        </h4>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem', marginBottom: 0 }}>
                          Activa este modo si estás lesionado o enfermo. La IA ignorará las caídas de rendimiento y adaptará sus consejos hacia la fisioterapia y regeneración.
                        </p>
                      </div>
                      <div 
                        onClick={handleToggleRecoveryMode}
                        style={{
                          width: '50px', height: '28px', borderRadius: '14px',
                          background: userProfile?.preferences?.recoveryMode ? '#f59e0b' : 'rgba(255,255,255,0.1)',
                          position: 'relative', cursor: 'pointer', transition: '0.3s'
                        }}
                      >
                        <div style={{
                          width: '24px', height: '24px', borderRadius: '50%', background: 'white',
                          position: 'absolute', top: '2px', left: userProfile?.preferences?.recoveryMode ? '24px' : '2px',
                          transition: '0.3s'
                        }} />
                      </div>
                    </div>
                  </div>

                  {userProfile?.preferences?.medicalContext ? (
                    <div style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '8px', padding: '1.5rem', position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0, color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <CheckCircle size={16} /> Contexto Clínico Activo
                        </h4>
                        <button 
                          onClick={handleDeleteMedicalReport}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}
                        >
                          <Trash size={14} /> Eliminar
                        </button>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#e2e8f0', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {userProfile.preferences.medicalContext}
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '8px', padding: '2rem', textAlign: 'center' }}>
                      <Upload size={32} color="rgba(255,255,255,0.3)" style={{ marginBottom: '1rem' }} />
                      <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        Sube un archivo PDF o una foto clara de tu informe.<br/>No guardamos el documento, solo extraemos los datos fisiológicos.
                      </p>
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.5rem',
                        background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid #10b981',
                        borderRadius: '8px', cursor: uploadingMedicalReport ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem',
                        transition: 'all 0.2s', opacity: uploadingMedicalReport ? 0.6 : 1
                      }}>
                        {uploadingMedicalReport ? (
                          <>Procesando con IA...</>
                        ) : (
                          <><Plus size={16} /> Subir Informe (PDF/IMG)</>
                        )}
                        <input 
                          type="file" 
                          accept="application/pdf,image/*" 
                          style={{ display: 'none' }} 
                          onChange={handleUploadMedicalReport}
                          disabled={uploadingMedicalReport}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <h3 className="text-lg" style={{ marginBottom: '1.5rem' }}>Credenciales de Acceso</h3>
                <form onSubmit={handleUpdateAccount} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Email</label>
                    <input 
                      type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} required
                      style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Nueva Contraseña (opcional)</label>
                    <input 
                      type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Dejar en blanco para mantener la actual"
                      style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Contraseña Actual (Requerida para guardar cambios)</label>
                    <input 
                      type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
                      style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
                    />
                  </div>
                  <button type="submit" disabled={loading} style={{
                    padding: '0.8rem', background: 'var(--health-cyan)', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '1rem'
                  }}>
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </form>

                <div style={{ marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <h3 className="text-lg" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Info size={20} color="var(--text-secondary)" /> Información Legal
                  </h3>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ padding: '0.8rem 1.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'background 0.2s' }}>
                      Política de Privacidad
                    </a>
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ padding: '0.8rem 1.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'background 0.2s' }}>
                      Condiciones de Uso
                    </a>
                  </div>
                </div>

                <div style={{ marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid rgba(239, 68, 68, 0.2)' }}>
                  <h3 className="text-lg" style={{ color: '#ef4444', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertTriangle size={20} /> Zona de Peligro
                  </h3>
                  {!deleteConfirm ? (
                    <button onClick={() => setDeleteConfirm(true)} style={{
                      padding: '0.6rem 1.2rem', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px', cursor: 'pointer'
                    }}>Eliminar mi cuenta permanentemente</button>
                  ) : (
                    <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '1.5rem', borderRadius: '8px' }}>
                      <p style={{ color: 'white', margin: '0 0 0.75rem 0' }}>Esta acción no se puede deshacer. Todos tus datos, actividades y progreso se borrarán de forma irreversible.</p>
                      <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.35)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.84rem', color: '#fde68a', lineHeight: 1.45 }}>
                        <strong>⚠️ Importante sobre tu suscripción (App Store / Google Play):</strong> Eliminar tu cuenta en NousMetrics borra tus datos personales, pero <strong>no cancela automáticamente</strong> la facturación en Apple App Store o Google Play. Recuerda cancelar tu suscripción activa en{' '}
                        <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', textDecoration: 'underline', fontWeight: 600 }}>Ajustes de Suscripciones (iOS)</a>{' '}o{' '}
                        <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', textDecoration: 'underline', fontWeight: 600 }}>Google Play</a>.
                      </div>
                      <input 
                        type="text" 
                        placeholder="Escribe tu contraseña o la palabra ELIMINAR" 
                        value={deletePassword} 
                        onChange={e => setDeletePassword(e.target.value)}
                        style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.5)', border: '1px solid #ef4444', borderRadius: '8px', color: 'white', outline: 'none', marginBottom: '1rem' }}
                      />
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button onClick={handleDeleteAccount} disabled={loading} style={{
                          padding: '0.6rem 1.2rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                        }}>Sí, Eliminar Definitivamente</button>
                        <button onClick={() => { setDeleteConfirm(false); setDeletePassword(''); }} style={{
                          padding: '0.6rem 1.2rem', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)', borderRadius: '8px', cursor: 'pointer'
                        }}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'membership' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <h3 className="text-lg" style={{ marginBottom: '1rem' }}>Gestión de Planes y Membresía</h3>
                <p className="text-muted text-sm" style={{ marginBottom: '2rem' }}>
                  Gestiona tu nivel de suscripción para desbloquear funciones avanzadas de análisis y biometría.
                </p>

                <div className="settings-grid-2col" style={{ marginBottom: '2.5rem' }}>
                  {/* Plan FREE */}
                  <div style={{
                    padding: '1.5rem',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: userProfile?.subscriptionTier !== 'PRO' ? '2px solid var(--health-cyan)' : '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative'
                  }}>
                    {userProfile?.subscriptionTier !== 'PRO' && (
                      <span style={{
                        position: 'absolute', top: '-12px', right: '1.5rem',
                        background: 'var(--health-cyan)', color: 'black',
                        padding: '0.25rem 0.75rem', borderRadius: '12px',
                        fontSize: '0.75rem', fontWeight: 'bold'
                      }}>Plan Activo</span>
                    )}
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', color: 'white' }}>Plan Inicial (FREE)</h4>
                      <div style={{ fontSize: '1.75rem', fontWeight: '800', color: 'white', marginBottom: '1rem' }}>
                        0 € <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: 'var(--text-muted)' }}>/ de por vida</span>
                      </div>
                      <ul style={{ paddingLeft: '1.25rem', margin: '0 0 2rem 0', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                        <li>Carga e historial de actividades</li>
                        <li>Metas semanales básicas</li>
                        <li>Estadísticas de kilometraje</li>
                        <li style={{ textDecoration: 'line-through', opacity: 0.5 }}>Análisis con Inteligencia Artificial</li>
                        <li style={{ textDecoration: 'line-through', opacity: 0.5 }}>Métricas avanzadas de sueño y HRV</li>
                      </ul>
                    </div>

                    {userProfile?.subscriptionTier === 'PRO' ? (
                      <button
                        onClick={() => {
                          alert('Para gestionar o cancelar tu suscripción a NousMetrics PRO, ve a Ajustes > Tu Nombre > Suscripciones en tu dispositivo Apple / Google Play.');
                        }}
                        style={{
                          width: '100%', padding: '0.75rem', background: 'transparent',
                          color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '8px',
                          cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', transition: 'all 0.2s'
                        }}
                      >
                        Gestionar o Cancelar en App Store
                      </button>
                    ) : (
                      <button
                        disabled
                        style={{
                          width: '100%', padding: '0.75rem', background: 'rgba(255,255,255,0.05)',
                          color: 'var(--text-muted)', border: 'none', borderRadius: '8px',
                          fontWeight: 'bold', fontSize: '0.9rem'
                        }}
                      >
                        Plan Actual
                      </button>
                    )}
                  </div>

                  {/* Plan PRO */}
                  <div style={{
                    padding: '1.5rem',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: userProfile?.subscriptionTier === 'PRO' ? '2px solid #ff5722' : '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative'
                  }}>
                    {userProfile?.subscriptionTier === 'PRO' && (
                      <span style={{
                        position: 'absolute', top: '-12px', right: '1.5rem',
                        background: '#ff5722', color: 'white',
                        padding: '0.25rem 0.75rem', borderRadius: '12px',
                        fontSize: '0.75rem', fontWeight: 'bold'
                      }}>Plan Activo</span>
                    )}
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', color: 'white' }}>Deportivo Avanzado (PRO)</h4>
                      <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#ff5722', marginBottom: '1rem' }}>
                        8,99 € <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: 'var(--text-muted)' }}>/ mes</span>
                      </div>
                      <ul style={{ paddingLeft: '1.25rem', margin: '0 0 2rem 0', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                        <li><strong>Análisis ilimitados con Inteligencia Artificial</strong></li>
                        <li><strong>Gráfico de Forma Atlética PMC (CTL/ATL/TSB)</strong></li>
                        <li><strong>Predisposición diaria al entrenamiento</strong></li>
                        <li>Monitoreo de fases del sueño y respiración</li>
                        <li>Soporte técnico preferente</li>
                      </ul>
                    </div>

                    {userProfile?.subscriptionTier !== 'PRO' ? (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent('open-pricing-modal'))}
                        style={{
                          width: '100%', padding: '0.85rem', background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
                          color: '#050810', border: 'none', borderRadius: '10px',
                          cursor: 'pointer', fontWeight: 800, fontSize: '0.95rem', transition: 'all 0.2s',
                          boxShadow: '0 4px 15px rgba(0, 229, 255, 0.35)'
                        }}
                      >
                        Subir a Plan PRO ⚡
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                        <button
                          disabled
                          style={{
                            width: '100%', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.15)',
                            color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px',
                            fontWeight: 'bold', fontSize: '0.9rem'
                          }}
                        >
                          Plan PRO Activo ✓
                        </button>
                        <button
                          onClick={() => window.dispatchEvent(new CustomEvent('open-pricing-modal'))}
                          style={{
                            width: '100%', padding: '0.4rem', background: 'transparent',
                            color: 'var(--text-secondary)', border: 'none',
                            cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline'
                          }}
                        >
                          Ver detalles o restaurar compras
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2rem' }}>
                  <h3 className="text-lg" style={{ marginBottom: '1rem' }}>Cupones de Descuento</h3>
                  <div style={{ display: 'flex', gap: '1rem', maxWidth: '400px' }}>
                    <input 
                      type="text" placeholder="Ej: PROMO2026" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      style={{ flex: 1, padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
                    />
                    <button 
                      onClick={() => {
                        if(couponCode) {
                          setError('Los cupones automáticos no están habilitados temporalmente. Contacta con soporte.');
                        }
                      }}
                      style={{
                        padding: '0.8rem 1.5rem', background: 'white', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer'
                      }}
                    >Aplicar</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && appMode === 'athlete' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <h3 className="text-lg" style={{ marginBottom: '1.5rem' }}>Accesos de Terceros</h3>
                <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
                  Controla quién tiene acceso de solo lectura a tus métricas de entrenamiento y salud.
                </p>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: 'white' }}>Entrenador Vinculado</h4>
                  {userProfile?.trainerRelation ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--health-cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'black', fontWeight: 'bold' }}>
                          {userProfile.trainerRelation.trainer.name.charAt(0)}
                        </div>
                        <div>
                          <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>{userProfile.trainerRelation.trainer.name}</p>
                          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Acceso concedido a métricas completas</p>
                        </div>
                      </div>
                      <button onClick={handleRevokeCoach} disabled={loading} style={{
                        padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem'
                      }}>
                        Revocar Acceso
                      </button>
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' }}>No tienes ningún entrenador vinculado a tu cuenta en este momento.</p>
                  )}
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--glass-border)', marginTop: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ShieldCheck size={18} color="var(--health-cyan)" /> Consentimiento de Datos de Salud (Art. 9 RGPD)
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
                    Puedes retirar en cualquier momento tu consentimiento para el tratamiento de biometría y salud (HRV, sueño, FC, SpO2). Si lo retiras, se detendrá el análisis fisiológico hasta que decidas reactivarlo.
                  </p>
                  <button
                    onClick={async () => {
                      const currentlyGranted = userProfile?.healthConsentGranted !== false;
                      if (currentlyGranted && !window.confirm('¿Deseas retirar tu consentimiento de datos de salud (Art. 9 RGPD)? Se pausará el procesamiento de métricas biométricas.')) return;
                      try {
                        setLoading(true);
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${API_URL}/auth/consent/health`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ granted: !currentlyGranted, policyVersion: '2026-10-30' })
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.error || 'Error al actualizar el consentimiento');
                        setMessage(data.message);
                        await fetchProfile();
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    style={{
                      padding: '0.6rem 1.2rem',
                      background: userProfile?.healthConsentGranted === false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.12)',
                      color: userProfile?.healthConsentGranted === false ? '#10b981' : '#ef4444',
                      border: `1px solid ${userProfile?.healthConsentGranted === false ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.85rem'
                    }}
                  >
                    {userProfile?.healthConsentGranted === false
                      ? 'Reactivar Consentimiento de Salud (Art. 9 RGPD)'
                      : 'Retirar Consentimiento de Salud (Art. 9 RGPD)'}
                  </button>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--glass-border)', marginTop: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={18} color="var(--health-cyan)" /> Aviso Matinal de Predisposición (07:30h)
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
                    Recibe un único aviso diario opcional por la mañana con tu índice de recuperación y zonas recomendadas, sin presión por rachas.
                  </p>
                  <button
                    onClick={async () => {
                      const prefs = typeof userProfile?.preferences === 'string'
                        ? JSON.parse(userProfile.preferences)
                        : (userProfile?.preferences || {});
                      const currentlyEnabled = Boolean(prefs?.morningPushEnabled || prefs?.notifications?.morningPushEnabled);
                      const nextState = !currentlyEnabled;
                      try {
                        setLoading(true);
                        const token = localStorage.getItem('token');
                        const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
                        const res = await fetch(`${API_URL}/auth/push-token`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            platform,
                            morningPushEnabled: nextState
                          })
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.error || 'Error al actualizar las notificaciones');
                        setMessage(data.message);
                        await fetchProfile();
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    style={{
                      padding: '0.6rem 1.2rem',
                      background: (userProfile?.preferences?.morningPushEnabled || userProfile?.preferences?.notifications?.morningPushEnabled)
                        ? 'rgba(239, 68, 68, 0.12)'
                        : 'rgba(16, 185, 129, 0.15)',
                      color: (userProfile?.preferences?.morningPushEnabled || userProfile?.preferences?.notifications?.morningPushEnabled)
                        ? '#ef4444'
                        : '#10b981',
                      border: `1px solid ${(userProfile?.preferences?.morningPushEnabled || userProfile?.preferences?.notifications?.morningPushEnabled) ? 'rgba(239, 68, 68, 0.35)' : 'rgba(16, 185, 129, 0.35)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.85rem'
                    }}
                  >
                    {(userProfile?.preferences?.morningPushEnabled || userProfile?.preferences?.notifications?.morningPushEnabled)
                      ? 'Desactivar Aviso Matinal'
                      : 'Activar Aviso Matinal (07:30h)'}
                  </button>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--glass-border)', marginTop: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Download size={18} /> Portabilidad de Datos (RGPD)
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                    Tienes derecho a descargar una copia de todos tus datos personales, actividades y biometría almacenados en nuestra plataforma en un formato estructurado (JSON).
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${API_URL}/user/export`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Error al exportar los datos');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `nousmetrics-export-${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                    style={{
                      padding: '0.6rem 1.2rem', background: 'var(--strava-orange)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', transition: 'filter 0.2s'
                    }}
                    onMouseOver={e => e.target.style.filter = 'brightness(1.1)'}
                    onMouseOut={e => e.target.style.filter = 'brightness(1)'}
                  >
                    Descargar mis datos
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'guide' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out', display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
                {/* 1. HERO BANNER */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.12) 0%, rgba(139, 92, 246, 0.12) 50%, rgba(16, 185, 129, 0.08) 100%)',
                  border: '1px solid rgba(0, 229, 255, 0.25)',
                  borderRadius: '16px',
                  padding: '1.4rem 1.6rem',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                    <div style={{ padding: '8px', background: 'rgba(0, 229, 255, 0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Sparkles size={22} color="var(--health-cyan)" />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'white' }}>
                        El Motor Fisiológico 360° con Inteligencia Artificial
                      </h3>
                      <span style={{ fontSize: '0.75rem', color: 'var(--health-cyan)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Fisiología Deportiva + Sensores Diarios + Modelo Coggan PMC
                      </span>
                    </div>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', lineHeight: '1.5', margin: 0, maxWidth: '780px' }}>
                    Nuestra plataforma unifica tus umbrales de rendimiento (pruebas de esfuerzo deportivas en PDF), tus dispositivos wearables (Apple Watch, Garmin, Withings) y las curvas matemáticas de carga para ofrecerte un copiloto inteligente que optimiza tu entrenamiento y recuperación.
                  </p>
                </div>

                {/* 2. BENTO GRID PRINCIPAL: 2 Columnas */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>

                  {/* BENTO 1: UMBRALES FISIOLÓGICOS Y PRUEBAS DE ESFUERZO */}
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.03)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '16px',
                    padding: '1.4rem',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ padding: '6px', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '8px' }}>
                          <Stethoscope size={18} color="#10b981" />
                        </div>
                        <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: 'bold' }}>
                          1. IA Deportiva Multimodal
                        </h4>
                      </div>
                      <span style={{ fontSize: '0.68rem', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399', padding: '3px 8px', borderRadius: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                        Exclusivo
                      </span>
                    </div>

                    <p style={{ color: 'var(--text-muted)', fontSize: '0.84rem', lineHeight: '1.45', marginBottom: '1rem' }}>
                      Sube el informe de tu prueba de esfuerzo o test de gases (PDF o foto). El asistente deportivo extrae tus zonas de entrenamiento:
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: 'auto' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.25)', padding: '0.6rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <FileText size={16} color="#34d399" style={{ flexShrink: 0 }} />
                        <div style={{ fontSize: '0.8rem', color: 'white' }}>
                          <strong>Umbrales Reales VT1 y VT2</strong>: Vatios y pulsaciones de umbral aeróbico y anaeróbico.
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.25)', padding: '0.6rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <Heart size={16} color="#f87171" style={{ flexShrink: 0 }} />
                        <div style={{ fontSize: '0.8rem', color: 'white' }}>
                          <strong>FC Máx & VO2Max Medidos</strong>: Sustituye fórmulas teóricas genéricas por tus datos medidos.
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.25)', padding: '0.6rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <ShieldCheck size={16} color="#60a5fa" style={{ flexShrink: 0 }} />
                        <div style={{ fontSize: '0.8rem', color: 'white' }}>
                          <strong>Asimilación y Recuperación</strong>: Ajusta tus zonas personalizadas según los datos de tu prueba.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* BENTO 2: PREDISPOSICIÓN DIARIA 360° */}
                  <div style={{
                    background: 'rgba(0, 229, 255, 0.03)',
                    border: '1px solid rgba(0, 229, 255, 0.25)',
                    borderRadius: '16px',
                    padding: '1.4rem',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ padding: '6px', background: 'rgba(0, 229, 255, 0.15)', borderRadius: '8px' }}>
                          <Gauge size={18} color="var(--health-cyan)" />
                        </div>
                        <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: 'bold' }}>
                          2. Algoritmo Readiness 360°
                        </h4>
                      </div>
                      <span style={{ fontSize: '0.68rem', background: 'rgba(0, 229, 255, 0.2)', color: 'var(--health-cyan)', padding: '3px 8px', borderRadius: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                        Transparente
                      </span>
                    </div>

                    <p style={{ color: 'var(--text-muted)', fontSize: '0.84rem', lineHeight: '1.45', marginBottom: '1rem' }}>
                      Metodología clínica desacoplada: Calculamos tu estado biológico interno (0-100) y, de forma independiente, prescribimos la dosis óptima de entrenamiento diario fundamentada en 4 pilares con vetos de seguridad:
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginTop: 'auto' }}>
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#818cf8' }}>35% Sueño (Hard Cap)</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: '1.3', marginTop: '2px' }}>
                          Duración y eficiencia. Si baja de 65, el readiness se limita a 70 y se bloquea la alta intensidad.
                        </div>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: 'var(--health-cyan)' }}>25% HRV rMSSD</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: '1.3', marginTop: '2px' }}>
                          Equilibrio autonómico vs tu pasillo móvil individual de 28 días (no medias genéricas).
                        </div>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#f59e0b' }}>25% Carga / TSB Coggan</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: '1.3', marginTop: '2px' }}>
                          Frescura neuromuscular (CTL - ATL de 7 días). Si TSB &lt; -20, alerta de fatiga acumulada.
                        </div>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '0.65rem 0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#34d399' }}>15% FCR &amp; SpO2</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: '1.3', marginTop: '2px' }}>
                          Frecuencia cardíaca en reposo basal y filtro de hipoxia (&lt;92% penaliza y alerta patología).
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NUEVO BENTO: LOS 5 PATRONES CLÍNICOS DE DECISIÓN DIARIA (A - E) */}
                <div style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(0, 229, 255, 0.2)',
                  borderRadius: '16px',
                  padding: '1.4rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ padding: '6px', background: 'rgba(0, 229, 255, 0.15)', borderRadius: '8px' }}>
                        <Target size={18} color="var(--health-cyan)" />
                      </div>
                      <div>
                        <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: 'bold' }}>
                          Árbol de Decisión: Los 5 Patrones Fisiológicos de Prescripción (A - E)
                        </h4>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          La IA cruza tus biomarcadores con los umbrales reales de tu prueba de esfuerzo (VT1 y VT2)
                        </span>
                      </div>
                    </div>
                    <span style={{ fontSize: '0.68rem', background: 'rgba(0, 229, 255, 0.15)', color: 'var(--health-cyan)', padding: '3px 10px', borderRadius: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                      Medicina Deportiva
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.85rem' }}>
                    {/* Patrón A */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid #10b981', borderRadius: '10px', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#34d399', fontSize: '0.86rem' }}>Patrón A: Preparación Excelente</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399', padding: '2px 6px', borderRadius: '8px', fontWeight: 600 }}>INTENSO / NORMAL</span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <strong>Criterio:</strong> Sueño &ge; 70, HRV en pasillo o elevado, FCR estable y TSB &gt; -15.<br/>
                        <strong>Prescripción:</strong> Series en VT2 / VO2Max (Z4-Z5) o rodaje de calidad. Sistema nervioso en equilibrio óptimo para asimilar fatiga aguda.
                      </p>
                    </div>

                    {/* Patrón B */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid #f59e0b', borderRadius: '10px', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#fbbf24', fontSize: '0.86rem' }}>Patrón B: Fatiga Acumulada por Carga</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', padding: '2px 6px', borderRadius: '8px', fontWeight: 600 }}>SUAVE Z1-Z2</span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <strong>Criterio:</strong> TSB &lt; -20 o ATL elevado de 7 días, con HRV cayendo o FCR subiendo.<br/>
                        <strong>Prescripción:</strong> 30-45 min estrictamente por debajo de VT1 aeróbico. Cero series. Acelera aclaramiento metabólico y recuperación del TSB.
                      </p>
                    </div>

                    {/* Patrón C */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid #3b82f6', borderRadius: '10px', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#60a5fa', fontSize: '0.86rem' }}>Patrón C: Sueño Insuficiente</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', padding: '2px 6px', borderRadius: '8px', fontWeight: 600 }}>MODERADO CORTO</span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <strong>Criterio:</strong> Sueño &lt; 65 o &lt; 6h, pero TSB estable y HRV aceptable.<br/>
                        <strong>Prescripción:</strong> Sesión controlada en Z2 (35-50 min). <em>Veto de Intensidad:</em> Prohíbe series anaeróbicas o tiradas largas por fatiga cognitiva/central.
                      </p>
                    </div>

                    {/* Patrón D */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid #8b5cf6', borderRadius: '10px', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#c084fc', fontSize: '0.86rem' }}>Patrón D: Discordancia Fisiológica</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(139, 92, 246, 0.2)', color: '#c084fc', padding: '2px 6px', borderRadius: '8px', fontWeight: 600 }}>RECUPERACIÓN</span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <strong>Criterio:</strong> TSB positivo (poco entreno registrado), pero HRV baja y FCR alta (+3-5 ppm).<br/>
                        <strong>Prescripción:</strong> Regenerativo suave o paseo. <em>La biología manda sobre las matemáticas:</em> estrés laboral, mala alimentación o alcohol no cuantificados en Strava.
                      </p>
                    </div>

                    {/* Patrón E */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid #ef4444', borderRadius: '10px', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: 'span 2' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#f87171', fontSize: '0.86rem' }}>Patrón E: Posible Enfermedad o Fatiga Sistémica</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', padding: '2px 6px', borderRadius: '8px', fontWeight: 600 }}>DESCANSO TOTAL</span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <strong>Criterio:</strong> SpO2 &lt; 92% o FC en reposo &ge; 7 ppm por encima de la media basal con desplome de HRV.<br/>
                        <strong>Prescripción:</strong> 0 min de ejercicio (Reposo absoluto en cama/sofá). Prioridad preventiva para evitar miocarditis o sobrecarga inmune aguda.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3. MATRIZ COMPARATIVA VS COMPETIDORES */}
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                    <div style={{ padding: '6px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '8px' }}>
                      <Zap size={18} color="#f59e0b" />
                    </div>
                    <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: 'bold' }}>
                      3. Comparativa de Mercado: ¿Por qué nuestra plataforma es única?
                    </h4>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(252, 76, 2, 0.2)', borderRadius: '12px', padding: '1.1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <span style={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>Vs. Strava</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(252,76,2,0.15)', color: '#FC4C02', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>Social vs Fisiología</span>
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4', margin: 0 }}>
                        Strava destaca en segmentos y comunidad, pero ignora el HRV, el descanso y los tests clínicos. Nosotros importamos Strava y le aportamos cerebro médico.
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '12px', padding: '1.1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <span style={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>Vs. TrainingPeaks</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(59,130,246,0.15)', color: '#3b82f6', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>IA vs Tablas Complejas</span>
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4', margin: 0 }}>
                        TrainingPeaks requiere pagar a un entrenador para descifrar gráficas frías. Nuestra IA traduce el PMC de Coggan en consejos claros y directos cada mañana.
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '1.1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <span style={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>Vs. WHOOP / Oura</span>
                        <span style={{ fontSize: '0.68rem', background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>Hardware Abierto</span>
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4', margin: 0 }}>
                        Exigen comprar su pulsera obligatoria y atarse a una cuota mensual. Nuestra plataforma es 100% agnóstica: compatible con Garmin, Apple Watch o Withings.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4. COPILOTO IA CONVERSACIONAL Y CASOS DE USO */}
                <div style={{
                  background: 'rgba(139, 92, 246, 0.04)',
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  borderRadius: '16px',
                  padding: '1.4rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                    <div style={{ padding: '6px', background: 'rgba(139, 92, 246, 0.15)', borderRadius: '8px' }}>
                      <Brain size={18} color="#a78bfa" />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: 'bold' }}>
                        4. Coach IA con Memoria Contextual
                      </h4>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        No es un chatbot genérico: conoce tus entrenamientos de hoy, tu fatiga semanal y tu prueba de esfuerzo
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.85rem' }}>
                    <div style={{ background: 'rgba(0,0,0,0.25)', padding: '1rem', borderRadius: '12px', borderLeft: '3px solid #3b82f6' }}>
                      <div style={{ fontWeight: 600, color: 'white', fontSize: '0.88rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Activity size={14} color="#3b82f6" /> Auditoría Post-Entreno
                      </div>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <em>"¿He forzado demasiado en las series de hoy teniendo en cuenta mi VT2 y mi HRV matutino?"</em> La IA audita tus zonas y programa tu recuperación.
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.25)', padding: '1rem', borderRadius: '12px', borderLeft: '3px solid #10b981' }}>
                      <div style={{ fontWeight: 600, color: 'white', fontSize: '0.88rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Sparkles size={14} color="#10b981" /> Estrategia de Picos (Tapering)
                      </div>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        <em>"Tengo una marcha ciclista el próximo mes. ¿Cómo planifico el tapering para llegar con TSB positivo?"</em> Ajusta volumen e intensidad automáticamente.
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.25)', padding: '1rem', borderRadius: '12px', borderLeft: '3px solid #f59e0b' }}>
                      <div style={{ fontWeight: 600, color: 'white', fontSize: '0.88rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertTriangle size={14} color="#f59e0b" /> Modo Lesión / Fisioterapia
                      </div>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                        Si activas el Modo Lesión, el Coach omite la exigencia de rendimiento y adapta sus consejos hacia la movilidad, fuerza preventiva y regeneración muscular.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'support' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out', display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 className="text-lg" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <LifeBuoy size={22} color="var(--health-cyan)" /> Soporte Técnico y Atención al Atleta
                    </h3>
                    <p className="text-muted text-sm" style={{ margin: '0.2rem 0 0 0' }}>
                      Resuelve incidencias de configuración, dudas de sincronización o contacta directamente con el equipo.
                    </p>
                  </div>
                </div>

                {/* Tarjeta de Contacto Directo */}
                <div style={{
                  background: 'radial-gradient(ellipse at top left, rgba(0, 229, 255, 0.12) 0%, rgba(15, 23, 42, 0.85) 100%)',
                  border: '1px solid rgba(0, 229, 255, 0.25)',
                  borderRadius: '16px',
                  padding: '2rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}>
                  <LifeBuoy size={52} color="#00e5ff" style={{ marginBottom: '1rem' }} />
                  <h4 style={{ fontSize: '1.25rem', color: 'white', margin: '0 0 0.5rem 0', fontWeight: 700 }}>¿Tienes alguna incidencia técnica o comercial?</h4>
                  <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto 1.5rem auto', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    Nuestro equipo técnico de desarrollo atiende directamente las solicitudes para garantizar el mejor servicio a los deportistas.
                  </p>
                  <a 
                    href="mailto:contacto@nousmetrics.com?subject=Soporte%20NousMetrics" 
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 2rem',
                      background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)', color: 'white', fontWeight: 'bold',
                      textDecoration: 'none', borderRadius: '30px', boxShadow: '0 4px 15px rgba(0,229,255,0.3)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.transform = 'scale(1.03)'}
                    onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Contactar con Soporte Técnico (Email)
                  </a>
                  <span style={{ marginTop: '0.75rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>contacto@nousmetrics.com</span>
                </div>

                {/* Preguntas Frecuentes de Solución de Problemas (Troubleshooting) */}
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '14px', padding: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1.2rem 0', color: 'white', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle2 size={18} color="#10b981" /> Preguntas Frecuentes y Solución de Incidencias
                  </h4>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', borderLeft: '3px solid #00e5ff' }}>
                      <h5 style={{ margin: '0 0 0.4rem 0', color: 'white', fontSize: '0.95rem' }}>📱 Mis datos de Apple Health / Google Health no aparecen hoy</h5>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.45' }}>
                        Abre la App Móvil durante 2 segundos cada mañana. La aplicación sincroniza nativamente en segundo plano tus datos de sueño, HRV y SpO2 acumulados la noche anterior.
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', borderLeft: '3px solid #FC4C02' }}>
                      <h5 style={{ margin: '0 0 0.4rem 0', color: 'white', fontSize: '0.95rem' }}>🚴 Mi actividad de Strava no se muestra</h5>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.45' }}>
                        Las actividades se importan automáticamente en tiempo real. Si acabas de finalizar un entrenamiento y tarda en aparecer, ve a <em>Apps y Dispositivos</em> y pulsa "Reconectar Strava".
                      </p>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', borderLeft: '3px solid #f59e0b' }}>
                      <h5 style={{ margin: '0 0 0.4rem 0', color: 'white', fontSize: '0.95rem' }}>⚠️ Mi Predisposición Diaria marca "Datos estimados"</h5>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.45' }}>
                        Esto indica que tu reloj no registró datos de HRV o descanso anoche. La plataforma activa temporalmente una estimación basada en tu fatiga histórica hasta que recibas la nueva medición.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'invitations' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div>
                    <h3 className="text-lg" style={{ margin: 0 }}>Gestión de Invitaciones</h3>
                    <p className="text-muted text-sm" style={{ margin: '0.2rem 0 0 0' }}>Administra los enlaces de registro para vincular a tus atletas.</p>
                  </div>
                </div>

                {loadingInvites ? (
                  <p className="text-muted text-sm">Cargando invitaciones...</p>
                ) : invitations.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed var(--glass-border)' }}>
                    <p className="text-muted text-sm" style={{ margin: 0 }}>No tienes invitaciones generadas.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {invitations.map(inv => (
                      <div key={inv.id} style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: '12px',
                        padding: '1.25rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '1rem', color: 'white' }}>{inv.name}</h4>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{inv.email}</span>
                          </div>
                          <span style={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            padding: '0.2rem 0.6rem',
                            borderRadius: '12px',
                            background: inv.isExpired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: inv.isExpired ? '#ef4444' : '#10b981'
                          }}>
                            {inv.isExpired ? 'Expirado' : 'Pendiente'}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '6px' }}>
                          <input 
                            type="text" 
                            readOnly 
                            value={inv.magicLink} 
                            style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text-muted)', fontSize: '0.8rem', outline: 'none' }} 
                          />
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(inv.magicLink);
                              alert('Enlace copiado al portapapeles');
                            }}
                            style={{ padding: '0.3rem 0.75rem', border: 'none', background: 'var(--health-cyan)', color: 'black', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}
                          >
                            Copiar Link
                          </button>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          <span>Creado el: {new Date(inv.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          <button 
                            onClick={() => handleRevokeInvitation(inv.id)}
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '600', padding: 0 }}
                          >
                            Revocar Invitación
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            {activeTab === 'coachHelp' && (
              <div style={{ animation: 'fadeInTab 0.3s ease-out', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <h3 className="text-lg" style={{ margin: 0 }}>Manual del Entrenador</h3>
                  <p className="text-muted text-sm" style={{ margin: '0.2rem 0 0 0' }}>Conoce a fondo las herramientas de NousMetrics para gestionar a tus atletas y monitorizar su rendimiento.</p>
                </div>

                {/* Tarjeta 1: Invitaciones */}
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <LinkIcon size={18} style={{ color: 'var(--health-cyan)' }} />
                    <h4 style={{ margin: 0, fontWeight: 'bold', color: 'white', fontSize: '0.95rem' }}>Enlaces Mágicos e Invitaciones</h4>
                  </div>
                  <p className="text-muted text-xs" style={{ margin: '0 0 0.75rem 0', lineHeight: '1.4' }}>
                    El proceso de alta de atletas es 100% autónomo. No tienes que registrar sus cuentas manualmente:
                  </p>
                  <ul style={{ paddingLeft: '1.2rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    <li><strong style={{ color: 'white' }}>Creación:</strong> Introduce el correo del atleta en la sección <em>Enviar Invitación</em> del panel principal.</li>
                    <li><strong style={{ color: 'white' }}>Enlace Mágico (Magic Link):</strong> Se genera una URL segura única. Copia el enlace en la pestaña de <em>Invitaciones</em> y envíaselo por WhatsApp o email.</li>
                    <li><strong style={{ color: 'white' }}>Vinculación:</strong> Cuando el atleta se registra usando ese link, su cuenta se enlaza directamente a tu perfil como coach.</li>
                    <li><strong style={{ color: 'white' }}>Control:</strong> Si la invitación caduca o te has equivocado, puedes revocarla en cualquier momento.</li>
                  </ul>
                </div>


                {/* Tarjeta 3: Configuración del Dashboard */}
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Settings size={18} style={{ color: '#3b82f6' }} />
                    <h4 style={{ margin: 0, fontWeight: 'bold', color: 'white', fontSize: '0.95rem' }}>Personalización de Ajustes Personales</h4>
                  </div>
                  <p className="text-muted text-xs" style={{ margin: 0, lineHeight: '1.4' }}>
                    Como entrenador, puedes decidir qué módulos analíticos quieres ver activos en el dashboard de cada atleta (por ejemplo, PMC, radar de cualidades o biometría de peso). Actívalos o desactívalos de forma global o individual desde el perfil del cliente para optimizar tu flujo de trabajo visual.
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
